import { type FC } from "react";
import { Box, TablePagination, CircularProgress, Stack } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useUsersQuery } from "@/partner/entities/users";
import { useFilter } from "@/partner/shared/components/Filter";
import { EmptyListCard } from "@/partner/shared/components/EmptyListCard";
import { useIsMobile } from "@/shared/hooks";
import { UserListItem } from "./UserListItem";
import { buildUsersFilter } from "../model/buildUsersFilter";

interface UsersListProps {
  onClickItem: (id: string) => void;
}

export const UsersList: FC<UsersListProps> = ({ onClickItem }) => {
  const { t } = useTranslation();
  const {
    onPageChange,
    onRowsPerPageChange,
    params: { page, rowsPerPage },
    filter,
  } = useFilter();

  const isMobile = useIsMobile("md");

  const { data, loading } = useUsersQuery({
    variables: {
      page,
      rowsPerPage,
      filter: buildUsersFilter(filter),
    },
  });

  const usersData = data?.users;
  const nodes = usersData?.nodes ?? [];
  const count = usersData?.count ?? 0;

  if (loading && !data) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (count === 0) {
    return <EmptyListCard title={t("users.list.empty")} />;
  }

  return (
    <Box>
      <Stack spacing={1}>
        {nodes.map((user) => (
          <UserListItem
            key={user.id}
            user={user}
            onClickItem={onClickItem}
            isMobile={isMobile}
          />
        ))}
      </Stack>

      <TablePagination
        component="div"
        count={count}
        page={page}
        onPageChange={onPageChange}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={onRowsPerPageChange}
        rowsPerPageOptions={[5, 10, 25, 100]}
        labelRowsPerPage={t("common.pagination.rowsPerPage")}
        labelDisplayedRows={({ from, to, count }) =>
          t("common.pagination.displayedRows", {
            from,
            to,
            count:
              count !== -1 ? count : t("common.pagination.moreThan", { to }),
          })
        }
      />
    </Box>
  );
};
